import rootStore from '../Stores'
import { attachmentPreview } from '../Helpers/attachmentPreview'
import { stripHtml } from '../Helpers/stripHtml'
import { truncateText } from '../Helpers/truncateText'
import {
    mapApiReactions,
    mapRealtimeChat,
    mapRealtimeMessage,
    toNotificationLevel,
    toTimestamp,
    type ApiReaction,
    type RealtimeChatPayload,
    type RealtimeDeliveryPayload,
    type RealtimeMessagePayload,
    type RealtimeTypingPayload,
} from '../Helpers/apiMappers'

/**
 * Envelope of chat realtime events published to the personal Centrifugo
 * channel by the backend ChatEventPublisher: { type: 'message:new', data: {...} }.
 */
export interface ChatEventEnvelope {
    type: string
    data?: unknown
}

/** Distinguishes chat envelopes from other publications (e.g. notifications) */
export const isChatRealtimeEvent = (data: unknown): data is ChatEventEnvelope => {
    if (typeof data !== 'object' || data === null) return false
    const type = (data as { type?: unknown }).type
    return (
        typeof type === 'string' &&
        (type.startsWith('message:') || type.startsWith('chat:') || type.startsWith('typing:'))
    )
}

export interface ChatEventHandlers {
    /** Toast for a private-chat message arriving off-screen; onOpen opens that chat */
    showToast?: (title: string, text: string, onOpen: () => void) => void
}

/**
 * Routes a chat realtime event into the stores. The sender never receives
 * message:new for own messages (they come in the REST response), so every
 * incoming message here is from another user.
 */
export const HandleChatEventTask = (envelope: ChatEventEnvelope, handlers: ChatEventHandlers = {}) => {
    const { chatStore, messageStore, uiStore, userStore } = rootStore

    switch (envelope.type) {
        case 'message:new': {
            const payload = (envelope.data ?? {}) as RealtimeMessagePayload
            const message = mapRealtimeMessage(payload)
            if (!message.id || !message.chatId) return

            messageStore.applyIncomingMessage(message)

            // Повідомлення прийшло — «друкує…» автора більше не актуальне
            chatStore.clearTyping(message.chatId, message.senderId)

            // Self-healing: a message for a chat we do not know about yet
            // (missed chat:new, fresh login in another tab) → reload the list
            const chat = chatStore.chats.find((c) => c.id === message.chatId)
            if (!chat) {
                void chatStore.loadChats()
                return
            }

            chatStore.updateLastMessage(
                message.chatId,
                stripHtml(message.content) || attachmentPreview(message.files),
                message.senderId,
                message.timestamp
            )

            const isChatOnScreen = uiStore.isDrawerOpen && uiStore.activeChatId === message.chatId
            if (isChatOnScreen) {
                // Юзер дивиться на чат: підтвердити читання БЕЗ guard'а по
                // бейджу (він тут завжди 0) — інакше вказівник на беку стоїть
                // і відправник не отримує ✓✓ (див. ChatStore.confirmChatRead)
                chatStore.confirmChatRead(message.chatId)
                break
            }

            chatStore.bumpUnread(message.chatId)

            // Toast matrix: a mention always toasts (any chat, any level);
            // otherwise any chat with the default 'all' level. Badge/muted
            // levels silence ordinary toasts.
            const mentioned = (payload.mentionedUserUuids ?? []).includes(userStore.currentUserId)
            const shouldToast = mentioned || chat.notificationLevel === 'all'

            if (shouldToast && handlers.showToast) {
                const senderName =
                    payload.senderName || userStore.getFullName(message.senderId)
                const isGroup = chat.type === 'group'
                const plainText =
                    truncateText(stripHtml(message.content), 80) || attachmentPreview(message.files)

                // Group toast carries the chat name, otherwise the sender is
                // ambiguous: title = group, body = "Sender: text"
                const title = mentioned
                    ? `${senderName} згадав(ла) вас`
                    : isGroup
                      ? (chat.name ?? senderName)
                      : senderName
                const text = isGroup ? `${senderName}: ${plainText}` : plainText

                handlers.showToast(title, text, () => {
                    uiStore.setActiveChatId(message.chatId)
                    uiStore.openDrawer()
                })
            }
            break
        }

        case 'message:updated': {
            const payload = (envelope.data ?? {}) as RealtimeMessagePayload
            if (!payload.uuid) return
            messageStore.applyMessageUpdated(payload.uuid, payload.content ?? '', payload.isEdited ?? true)
            break
        }

        case 'message:deleted': {
            const payload = (envelope.data ?? {}) as RealtimeMessagePayload
            if (!payload.uuid) return
            messageStore.applyMessageDeleted(payload.uuid)
            break
        }

        case 'message:reaction': {
            const payload = (envelope.data ?? {}) as { uuid?: string; reactions?: ApiReaction[] }
            if (!payload.uuid) return
            messageStore.applyReactions(payload.uuid, mapApiReactions(payload.reactions))
            break
        }

        case 'message:delivery': {
            // Хтось дочитав чат: інші учасники малюють ✓✓; якщо це я сам
            // з іншої вкладки — локально гаситься бейдж непрочитаного
            const payload = (envelope.data ?? {}) as RealtimeDeliveryPayload
            if (!payload.chatUuid || !payload.userUuid) return

            chatStore.applyReadState(payload.chatUuid, payload.userUuid, toTimestamp(payload.lastReadAt))
            if (payload.userUuid === userStore.currentUserId) {
                chatStore.resetUnreadLocal(payload.chatUuid)
            }
            break
        }

        case 'typing:start': {
            // Стоп-події немає: індикатор живе TYPING_TTL_MS і гаситься
            // сам, або миттєво по message:new цього ж автора
            const payload = (envelope.data ?? {}) as RealtimeTypingPayload
            if (!payload.chatUuid || !payload.userUuid) return

            chatStore.setTyping(
                payload.chatUuid,
                payload.userUuid,
                payload.firstName || userStore.getFullName(payload.userUuid)
            )
            break
        }

        case 'chat:updated': {
            // Власник змінив назву/аватарку — перемальовуємо у всіх
            const payload = (envelope.data ?? {}) as { uuid?: string; name?: string | null; avatarUrl?: string | null }
            if (!payload.uuid) return
            chatStore.applyChatUpdated(payload.uuid, payload.name ?? null, payload.avatarUrl ?? null)
            break
        }

        case 'chat:members': {
            // Склад змінився; якщо серед видалених — я, чат зникає зі списку
            const payload = (envelope.data ?? {}) as {
                chatUuid?: string
                participantUuids?: string[]
                addedUserUuids?: string[]
                removedUserUuids?: string[]
            }
            if (!payload.chatUuid) return

            // Self-healing: мене додали в чат, якого ще немає локально
            // (страховка на випадок, якщо chat:new загубився або прийшов
            // у іншому порядку) — перезавантажуємо список чатів
            const participantUuids = payload.participantUuids ?? []
            const isUnknownChat = !chatStore.chats.some((c) => c.id === payload.chatUuid)
            if (isUnknownChat && participantUuids.includes(userStore.currentUserId)) {
                void chatStore.loadChats()
                break
            }

            chatStore.applyChatMembers(
                payload.chatUuid,
                participantUuids,
                payload.removedUserUuids ?? []
            )

            // Нові учасники можуть бути поза реєстром контактів
            const unknown = (payload.addedUserUuids ?? []).some((id) => !userStore.getById(id))
            if (unknown) {
                void userStore.loadUsers()
            }
            break
        }

        case 'chat:deleted': {
            const payload = (envelope.data ?? {}) as { uuid?: string }
            if (!payload.uuid) return
            chatStore.applyChatDeleted(payload.uuid)
            break
        }

        case 'chat:notifications': {
            // Own setting changed in another tab/device of the same user
            const payload = (envelope.data ?? {}) as { chatUuid?: string; level?: string }
            if (!payload.chatUuid) return
            chatStore.applyNotificationLevel(payload.chatUuid, toNotificationLevel(payload.level))
            break
        }

        case 'chat:new': {
            const payload = (envelope.data ?? {}) as RealtimeChatPayload
            const chat = mapRealtimeChat(payload)
            if (!chat.id) return

            chatStore.upsertChat(chat)

            // Participants we have never seen yet would render as "Невідомий" —
            // refresh the contacts registry in the background
            const hasUnknownUsers = chat.participantIds.some((id) => !userStore.getById(id))
            if (hasUnknownUsers) {
                void userStore.loadUsers()
            }
            break
        }

        default:
            // Unknown chat event — deliberately ignored (forward compatibility)
            break
    }
}
